import React, { useState, useMemo, useEffect } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCalendar,
  FiCheckCircle,
  FiAlertCircle,
  FiVideo,
  FiMapPin,
} from 'react-icons/fi';

// Timezone-safe date formatter (YYYY-MM-DD)
export const formatDateKey = (date) => {
  if (!date) return '';
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDisplayDate = (dateKey) => {
  if (!dateKey) return '';
  const [year, month, day] = dateKey.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const CalenderAndTimeSlot = ({
  selectedDoctor,
  formData,
  setFormData,
  getAllAppointmentData,
  isOnlineFilter,
}) => {
  const todayKey = useMemo(() => formatDateKey(new Date()), []);
  const selectedDateKey = formData?.bookDate || todayKey;

  // Active month in calendar view (separated from selected date)
  const [viewDate, setViewDate] = useState(() => {
    if (selectedDateKey) {
      const [y, m, d] = selectedDateKey.split('-').map(Number);
      return new Date(y, m - 1, d || 1);
    }
    return new Date();
  });

  // Ensure formData has an initial bookDate if empty
  useEffect(() => {
    if (!formData?.bookDate) {
      setFormData((prev) => ({ ...prev, bookDate: todayKey }));
    }
  }, [formData?.bookDate, todayKey, setFormData]);

  const doctor = selectedDoctor?.[0] || null;
  const availability = doctor?.availability || {};

  // Extract available days
  const availableDays = useMemo(() => {
    const list = [];
    Object.entries(availability || {}).forEach(([dateKey, dateValue]) => {
      if (dateValue?.a_status === 'Available') {
        list.push(dateKey);
      }
    });
    return list;
  }, [availability]);

  // Booked appointments for the selected date
  const bookedAppointments = useMemo(() => {
    if (!getAllAppointmentData || !Array.isArray(getAllAppointmentData)) return [];
    return getAllAppointmentData
      .filter((e) => {
        const bDate = e?.bookDate ? formatDateKey(e.bookDate) : '';
        return bDate === selectedDateKey && e?.status !== 'Cancelled';
      })
      .map((e) => ({
        patient: e?.patient?.id,
        bookedTime: e?.bookTimeSlot?._id,
        startTime: e?.bookTimeSlot?.startTime,
        endTime: e?.bookTimeSlot?.endTime,
        bookedStatus: e?.status || 'Booked',
        name: e?.patient?.name,
      }));
  }, [getAllAppointmentData, selectedDateKey]);

  // Slots for the selected date
  const timeSlots = availability?.[selectedDateKey]?.timeSlots || [];

  const handleSelectDate = (dateKey) => {
    setFormData((prev) => ({
      ...prev,
      bookDate: dateKey,
      bookTimeSlot: prev.bookDate === dateKey ? prev.bookTimeSlot : '',
    }));
  };

  const isSelectedDateAvailable = availableDays.includes(selectedDateKey);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
          <FiCalendar className="text-sky-600" />
          <span>Appointment Date & Time Slot</span>
        </label>
        <span className="text-xs text-gray-500">
          Mode:{' '}
          <strong className={isOnlineFilter ? 'text-sky-600' : 'text-emerald-600'}>
            {isOnlineFilter ? 'Online Video Consultation' : 'In-Clinic Visit'}
          </strong>
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* Calendar View (5 cols on lg) */}
        <div className="lg:col-span-6 xl:col-span-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-xs">
          <CalenderView
            viewDate={viewDate}
            setViewDate={setViewDate}
            selectedDateKey={selectedDateKey}
            onSelectDate={handleSelectDate}
            availableDays={availableDays}
            todayKey={todayKey}
            availability={availability}
            isOnlineFilter={isOnlineFilter}
          />
        </div>

        {/* Time Slots (7 cols on lg) */}
        <div className="lg:col-span-6 xl:col-span-7 rounded-2xl border border-gray-200 bg-white p-4 shadow-xs">
          <TimeSlots
            selectedDateKey={selectedDateKey}
            setFormData={setFormData}
            formData={formData}
            timeSlots={timeSlots}
            bookedAppointments={bookedAppointments}
            isOnlineFilter={isOnlineFilter}
            isAvailableDay={isSelectedDateAvailable}
            doctorName={doctor?.name}
          />
        </div>
      </div>
    </div>
  );
};

const CalenderView = ({
  viewDate,
  setViewDate,
  selectedDateKey,
  onSelectDate,
  availableDays,
  todayKey,
  availability,
  isOnlineFilter,
}) => {
  const currentYear = viewDate.getFullYear();
  const currentMonth = viewDate.getMonth();

  const daysInMonth = useMemo(() => {
    return new Date(currentYear, currentMonth + 1, 0).getDate();
  }, [currentYear, currentMonth]);

  const firstDayWeekday = useMemo(() => {
    return new Date(currentYear, currentMonth, 1).getDay();
  }, [currentYear, currentMonth]);

  const changeMonth = (delta) => {
    setViewDate(new Date(currentYear, currentMonth + delta, 1));
  };

  const jumpToToday = () => {
    const today = new Date();
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
    onSelectDate(todayKey);
  };

  const renderDays = () => {
    const days = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const monthStr = String(currentMonth + 1).padStart(2, '0');
      const dayStr = String(day).padStart(2, '0');
      const dayKey = `${currentYear}-${monthStr}-${dayStr}`;

      const isToday = dayKey === todayKey;
      const isSelected = dayKey === selectedDateKey;
      const isPast = dayKey < todayKey;
      const isAvailable = availableDays.includes(dayKey);

      // Check slot count
      const slots = availability?.[dayKey]?.timeSlots || [];
      const matchingSlots = slots.filter((s) =>
        isOnlineFilter !== undefined ? Boolean(s?.isOnline) === Boolean(isOnlineFilter) : true
      );
      const hasMatchingSlots = matchingSlots.length > 0;

      let btnClass = 'border bg-white text-gray-700 hover:border-sky-400 hover:bg-sky-50/50';

      if (isPast) {
        btnClass = 'border-transparent text-gray-300 bg-gray-50/60 cursor-not-allowed opacity-50';
      } else if (isSelected) {
        btnClass =
          'border-sky-600 bg-sky-600 text-white font-bold shadow-sm ring-2 ring-sky-200';
      } else if (isAvailable && hasMatchingSlots) {
        btnClass =
          'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 font-semibold';
      } else if (isAvailable) {
        btnClass =
          'border-emerald-200 bg-emerald-50/40 text-emerald-700 hover:bg-emerald-100/60 font-medium';
      } else if (isToday) {
        btnClass = 'border-sky-400 text-sky-700 bg-sky-50/60 font-semibold';
      }

      days.push(
        <button
          key={day}
          type="button"
          disabled={isPast}
          onClick={() => onSelectDate(dayKey)}
          title={
            isPast
              ? 'Past dates cannot be booked'
              : isAvailable
              ? `${dayKey}: Doctor Available (${matchingSlots.length} slot${matchingSlots.length !== 1 ? 's' : ''})`
              : `${dayKey}: Click to check slots`
          }
          className={`
            relative flex flex-col items-center justify-center
            h-10 sm:h-11 rounded-xl text-xs sm:text-sm font-medium
            transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-sky-400
            ${btnClass}
          `}
        >
          <span>{day}</span>

          {/* Dots / Indicators */}
          <div className="flex items-center gap-0.5 mt-0.5">
            {isAvailable && hasMatchingSlots && !isSelected && (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            )}
            {isToday && !isSelected && (
              <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
            )}
          </div>
        </button>
      );
    }

    return days;
  };

  return (
    <div className="w-full">
      {/* Month Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <button
          type="button"
          onClick={() => changeMonth(-1)}
          className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition cursor-pointer"
          title="Previous month"
        >
          <FiChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2">
          <span className="font-bold text-gray-800 text-sm sm:text-base">
            {viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </span>
          <button
            type="button"
            onClick={jumpToToday}
            className="text-[11px] font-semibold text-sky-600 hover:text-sky-800 hover:underline px-1.5 py-0.5 rounded bg-sky-50"
          >
            Today
          </button>
        </div>

        <button
          type="button"
          onClick={() => changeMonth(1)}
          className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition cursor-pointer"
          title="Next month"
        >
          <FiChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Weekday Labels */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 text-center mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName, idx) => (
          <div
            key={idx}
            className={`text-[11px] font-bold py-1 ${
              idx === 0 || idx === 6 ? 'text-rose-500' : 'text-gray-400'
            }`}
          >
            {dayName}
          </div>
        ))}
      </div>

      {/* Grid of Days */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 text-sm">
        {Array.from({ length: firstDayWeekday }, (_, i) => (
          <div key={`offset-${i}`} className="h-10 sm:h-11" />
        ))}
        {renderDays()}
      </div>

      {/* Calendar Legend */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-500">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-md bg-sky-600" />
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-md bg-emerald-100 border border-emerald-300" />
          <span>Available</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-sky-500" />
          <span>Today</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-md bg-gray-100 opacity-60" />
          <span>Past</span>
        </div>
      </div>
    </div>
  );
};

const TimeSlots = ({
  selectedDateKey,
  setFormData,
  formData,
  timeSlots,
  bookedAppointments,
  isOnlineFilter,
  isAvailableDay,
  doctorName,
}) => {
  const handleSelectSlot = (slot) => {
    setFormData((prev) => ({
      ...prev,
      bookDate: selectedDateKey,
      bookTimeSlot: slot,
      status: 'Booked',
    }));
  };

  const filteredSlots = useMemo(() => {
    if (!timeSlots || !Array.isArray(timeSlots)) return [];
    return timeSlots.filter((slot) => {
      if (isOnlineFilter !== undefined) {
        return Boolean(slot?.isOnline) === Boolean(isOnlineFilter);
      }
      return true;
    });
  }, [timeSlots, isOnlineFilter]);

  const selectedSlotId = formData?.bookTimeSlot?._id;
  const selectedSlotStart = formData?.bookTimeSlot?.startTime;

  return (
    <div className="w-full flex flex-col h-full">
      {/* Header bar */}
      <div className="mb-3 pb-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center font-bold">
            <FiClock className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-xs sm:text-sm font-bold text-gray-800">
              {formatDisplayDate(selectedDateKey)}
            </h4>
            <p className="text-[11px] text-gray-500">
              {filteredSlots.length} slot{filteredSlots.length !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>

        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
            isOnlineFilter
              ? 'bg-sky-50 text-sky-700 border border-sky-200'
              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          }`}
        >
          {isOnlineFilter ? <FiVideo className="w-3.5 h-3.5" /> : <FiMapPin className="w-3.5 h-3.5" />}
          {isOnlineFilter ? 'Online Video' : 'In-Clinic Visit'}
        </span>
      </div>

      {/* Slots Grid or Empty State */}
      {filteredSlots.length === 0 ? (
        <div className="py-12 px-4 flex flex-col items-center justify-center text-center text-gray-400 bg-gray-50/50 rounded-xl border border-dashed border-gray-200 my-auto">
          <FiClock className="w-10 h-10 text-gray-300 mb-2 stroke-1" />
          <p className="font-semibold text-gray-700 text-sm">No Time Slots Available</p>
          <p className="text-xs text-gray-400 mt-1 max-w-sm">
            {doctorName ? `${doctorName} has` : 'There are'} no {isOnlineFilter ? 'online' : 'in-clinic'}{' '}
            slots scheduled for {formatDisplayDate(selectedDateKey)}. Try picking another date on the calendar.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 xl:grid-cols-4 gap-2.5 max-h-[360px] overflow-y-auto pr-1 content-start auto-rows-max">
          {filteredSlots.map((slot) => {
            const bookedItem = bookedAppointments.find(
              (i) =>
                (slot._id && i.bookedTime === slot._id) ||
                (slot.startTime && i.startTime === slot.startTime && i.endTime === slot.endTime)
            );

            const isBooked = Boolean(bookedItem);
            const isSelected =
              (selectedSlotId && selectedSlotId === slot._id) ||
              (!selectedSlotId && selectedSlotStart && selectedSlotStart === slot.startTime);

            let cardClass =
              'border border-gray-200 bg-white text-gray-800 hover:border-emerald-400 hover:bg-emerald-50/40 hover:shadow-xs';

            if (isBooked) {
              cardClass =
                'border-rose-200 bg-rose-50/70 text-rose-700 cursor-not-allowed opacity-60';
            } else if (isSelected) {
              cardClass =
                'border-sky-600 bg-sky-600 text-white shadow-md ring-2 ring-sky-300 font-semibold';
            }

            return (
              <button
                key={slot._id || `${slot.startTime}-${slot.endTime}`}
                type="button"
                disabled={isBooked}
                onClick={() => handleSelectSlot(slot)}
                title={
                  isBooked
                    ? `Already booked by ${bookedItem?.name || 'another patient'}`
                    : `Select ${slot.startTime} – ${slot.endTime}`
                }
                className={`
                  rounded-xl p-2.5 text-center transition-all duration-150
                  flex flex-col items-center justify-between gap-1 cursor-pointer
                  focus:outline-none focus:ring-2 focus:ring-sky-400
                  ${cardClass}
                `}
              >
                {/* Time range */}
                <span className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                  {slot.startTime} – {slot.endTime}
                </span>

                {/* Mode badge */}
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    isSelected
                      ? 'bg-sky-700 text-sky-100'
                      : slot.isOnline
                      ? 'bg-sky-50 text-sky-700'
                      : 'bg-emerald-50 text-emerald-700'
                  }`}
                >
                  {slot.isOnline ? '🌐 Online' : '🏥 In-Clinic'}
                </span>

                {/* Status */}
                <span
                  className={`text-[11px] font-medium mt-0.5 ${
                    isSelected
                      ? 'text-sky-100 font-bold'
                      : isBooked
                      ? 'text-rose-600'
                      : 'text-emerald-600'
                  }`}
                >
                  {isBooked ? 'Booked' : isSelected ? '✓ Selected' : 'Available'}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Selected Slot Notice */}
      {formData?.bookTimeSlot && formData?.bookDate === selectedDateKey && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs bg-sky-50/70 p-2.5 rounded-xl border border-sky-100">
          <div className="flex items-center gap-2 text-sky-900">
            <FiCheckCircle className="w-4 h-4 text-sky-600 shrink-0" />
            <span>
              Selected Slot: <strong>{formData.bookTimeSlot.startTime} – {formData.bookTimeSlot.endTime}</strong>
            </span>
          </div>
          <span className="font-semibold text-sky-700 text-[11px]">Ready</span>
        </div>
      )}
    </div>
  );
};

export default CalenderAndTimeSlot;
